import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar, GripVertical } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
} from '@jira-clone/ui'
import { useTermConditions, useTermConditionMutations } from '../../shared/hooks/useTermConditions'
import { getApiErrorMessage } from '../../shared/utils/apiError'

interface DraftState {
  id?: string
  title: string
  content: string
  category: string
  displayOrder: number
  isDefault: boolean
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  title: '',
  content: '',
  category: 'GENERAL',
  displayOrder: 0,
  isDefault: false,
  isActive: true,
}

const CATEGORIES = ['GENERAL', 'PAYMENT', 'DELIVERY', 'LEGAL', 'WARRANTY', 'SUPPORT', 'CANCELLATION', 'OTHER']

export default function TermConditionAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? {} : { isActive: value })
  }

  const { data: terms = [], isLoading } = useTermConditions({
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove, setDefault } = useTermConditionMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft({ ...EMPTY_DRAFT, displayOrder: (terms.length || 0) + 1 })
    setDialogOpen(true)
  }

  const openEdit = (term: any) => {
    setDraft({
      id: term.id,
      title: term.title,
      content: term.content || '',
      category: term.category || 'GENERAL',
      displayOrder: term.displayOrder || 0,
      isDefault: term.isDefault,
      isActive: term.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.title.trim()) {
      toast.error('Title is required.')
      return
    }
    if (!draft.content.trim()) {
      toast.error('Content is required.')
      return
    }
    try {
      const payload = {
        title: draft.title.trim(),
        content: draft.content.trim(),
        category: draft.category,
        displayOrder: draft.displayOrder,
        isDefault: draft.isDefault,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Term updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Term created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this term' : 'create this term'))
    }
  }

  const handleDelete = async (term: any) => {
    if (!confirm(`Delete "${term.title}"? This can't be undone.`)) return
    setDeletingId(term.id)
    try {
      await remove.mutateAsync(term.id)
      toast.success('Term deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this term'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (term: any) => {
    try {
      await setStatus.mutateAsync({ id: term.id, isActive: !term.isActive })
      toast.success(term.isActive ? 'Term deactivated.' : 'Term activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle term status'))
    }
  }

  const handleSetDefault = async (term: any) => {
    try {
      await setDefault.mutateAsync(term.id)
      toast.success('Default term updated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'set as default'))
    }
  }

  const sortedTerms = [...terms].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Terms & Conditions</h2>
          <p className="text-sm text-muted-foreground">Manage standard terms and conditions for quotations and contracts.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Term
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search terms..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : sortedTerms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No terms yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {sortedTerms.map((term, index) => (
            <Card key={term.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground shrink-0 w-6 text-center font-mono">
                        {term.displayOrder || index + 1}
                      </span>
                      <Badge variant={term.isActive ? 'default' : 'secondary'} className="text-xs">
                        {term.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {term.isDefault && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{term.title}</p>
                        {term.category && (
                          <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                            {term.category}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate line-clamp-2">
                        {term.content}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(term)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {term.isDefault ? (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(term)}
                        title="Set as Default"
                      >
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(term)}
                      title={term.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {term.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(term)}
                      disabled={deletingId === term.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Term' : 'New Term'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tc-title">Title *</Label>
                <Input
                  id="tc-title"
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder="e.g. Payment Terms"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tc-category">Category</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tc-order">Display Order</Label>
                <Input
                  id="tc-order"
                  type="number"
                  min={0}
                  value={draft.displayOrder}
                  onChange={(e) => setDraft((d) => ({ ...d, displayOrder: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tc-content">Content *</Label>
              <Textarea
                id="tc-content"
                value={draft.content}
                onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                placeholder="Full terms and conditions text"
                rows={5}
              />
            </div>

            <Separator className="my-2" />

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  id="tc-default"
                  type="checkbox"
                  checked={draft.isDefault}
                  onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
                />
                <label htmlFor="tc-default" className="cursor-pointer">Set as default</label>
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Term'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}